import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CurrencyCode,
  OrderStatus,
  UserRole,
  convertUsdMinorToVesMinor,
  formatMoney,
} from '@lombok/shared';
import type { Order, OrderItem, OrderView, UserRole as UserRoleType, WarehouseOrderItem } from '@lombok/shared';
import type { OrderReservation } from '@lombok/api-client';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@lombok/ui';
import { AccountName } from '@/components/AccountName';
import { WIDE_COLUMN } from '@/components/layout';
import { PageHeader } from '@/components/PageHeader';
import { BackLink, DetailRow } from '@/components/DetailRow';
import { StatusPill } from '@/components/StatusPill';
import { QueryState } from '@/components/QueryState';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { TextareaField } from '@/components/form';
import { useSessionProfile } from '@/auth/useSessionProfile';
import {
  useApproveOrder,
  useCancelOrder,
  useCreateInvoiceFromOrder,
  useDeliverOrder,
  useDispatchOrder,
  useOrder,
  usePickOrder,
  useRejectOrder,
  useSubmitOrder,
} from '@/data/hooks';
import { orderLabel, placeLabel } from '@/lib/names';
import { isPriced, pricedItems } from '@/lib/orderView';
import { approvalReasonLabel } from './labels';
import { RepeatOrderButton } from './RepeatOrderButton';
import { INTERNAL_ACTOR_ROLES, canCancelOrder } from './cancel';
import { canApproveOrders } from './approval';

/** Roles that may generate a billing document from an order (mirrors backend — UX gate only). */
const BILLING_ROLES: readonly UserRoleType[] = [
  UserRole.SUPER_ADMIN,
  UserRole.ADMIN,
  UserRole.FINANCE,
];

/** Order statuses that cannot be billed (nothing committed yet, or it was killed). */
const NON_BILLABLE: readonly OrderStatus[] = [
  OrderStatus.DRAFT,
  OrderStatus.CANCELLED,
  OrderStatus.REJECTED,
];

/** Roles that may prepare/dispatch in the warehouse (mirrors backend — UX gate only). */
const FULFILLMENT_ROLES: readonly UserRoleType[] = [
  UserRole.SUPER_ADMIN,
  UserRole.ADMIN,
  UserRole.WAREHOUSE,
];
/** Roles that may confirm delivery (warehouse/admin + drivers). */
const DELIVERY_ROLES: readonly UserRoleType[] = [...FULFILLMENT_ROLES, UserRole.DRIVER];

interface OrderDetailPageProps {
  /** Group root for the "Pedidos" back link, e.g. "/app/admin/pedidos". */
  basePath: string;
  backLabel?: string;
}

/**
 * Pedido detail: header + items (line/total money) + locked-rate VES totals +
 * reservations + a derived status summary. Role-gated ACTIONS (submit / approve
 * / reject / cancel) are UX affordances only — the backend re-checks every
 * transition and is the sole authority.
 */
export function OrderDetailPage({ basePath, backLabel = 'Pedidos' }: OrderDetailPageProps) {
  const { id } = useParams<{ id: string }>();
  const query = useOrder(id);
  const order = query.data?.order;

  // Sibling "facturas" route under the same group root (e.g. /app/admin/pedidos → /app/admin/facturas).
  const invoicesBase = basePath.replace(/\/[^/]+$/, '/facturas');

  return (
    <div className={`flex flex-col gap-6 ${WIDE_COLUMN}`}>
      <div className="flex flex-col gap-2">
        <BackLink to={basePath}>{backLabel}</BackLink>
        <PageHeader
          title={order ? `Pedido ${orderLabel(order)}` : 'Pedido'}
          description={order && !isPriced(order) ? "Detalle y estado." : "Detalle, montos y estado."}
          actions={
            order ? (
              <div className="flex flex-wrap items-center gap-2">
                <StatusPill kind="order" value={order.status} />
                <GenerateInvoiceAction order={order} invoicesBase={invoicesBase} />
                <RepeatOrderButton orderId={order.orderId} />
              </div>
            ) : undefined
          }
        />
      </div>

      <QueryState
        isLoading={query.isLoading}
        isError={query.isError}
        error={query.error}
        onRetry={() => void query.refetch()}
        loadingLabel="Cargando pedido…"
      >
        {query.data && order ? (
          <div className="flex flex-col gap-6">
            <OrderActions order={order} />
            <FulfillmentActions order={order} />
            {order.requiresApproval && order.status === OrderStatus.PENDING_APPROVAL ? (
              <ApprovalReasons reasons={order.approvalReasons ?? []} />
            ) : null}
            {isPriced(order) ? (
              <OrderItemsCard items={pricedItems(query.data.items)} order={order} />
            ) : (
              <PickItemsCard items={query.data.items} />
            )}
            <div className="grid gap-6 lg:grid-cols-2">
              <OrderSummaryCard
                order={order}
                customerName={query.data.customerName}
                salesRepName={query.data.salesRepName}
              />
              {query.data.reservations ? (
                <ReservationsCard reservations={query.data.reservations} items={query.data.items} />
              ) : null}
            </div>
          </div>
        ) : null}
      </QueryState>
    </div>
  );
}

/** Warning panel listing why the order needs approval (Spanish reason labels). */
function ApprovalReasons({ reasons }: { reasons: string[] }) {
  const { profile } = useSessionProfile();
  // The customer who placed the order reads the reasons about their own account, not the staff wording.
  const own = profile?.role === UserRole.CUSTOMER_USER;
  return (
    <div className="rounded-md border border-warning-soft bg-warning-soft/50 p-4">
      <p className="text-sm font-medium text-text-primary">Este pedido requiere aprobación.</p>
      {reasons.length > 0 ? (
        <ul className="mt-2 list-inside list-disc text-sm text-text-muted">
          {reasons.map((code) => (
            <li key={code}>{approvalReasonLabel(code, own)}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** Items table with per-line and total money (shared formatMoney). */
function OrderItemsCard({ items, order }: { items: OrderItem[]; order: Order }) {
  const vesTotal = convertUsdMinorToVesMinor(order.totalMinorUsd, order.exchangeRate.rate);
  return (
    <Card className="overflow-hidden">
      <CardHeader>
        <CardTitle>Artículos</CardTitle>
      </CardHeader>
      <CardContent className="px-0 pb-0 pt-0">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-border text-xs uppercase tracking-wide text-text-muted">
                <th className="px-4 py-3 font-medium">Producto</th>
                <th className="px-4 py-3 font-medium">SKU</th>
                <th className="px-4 py-3 text-right font-medium">Cant.</th>
                <th className="px-4 py-3 text-right font-medium">Precio</th>
                <th className="px-4 py-3 text-right font-medium">Total línea</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.lineNumber} className="border-b border-border last:border-0">
                  <td className="px-4 py-3 font-medium text-text-primary">{it.productName}</td>
                  <td className="px-4 py-3 font-mono text-xs text-text-muted">{it.skuCode}</td>
                  <td className="px-4 py-3 text-right text-text-muted">{it.quantity}</td>
                  <td className="px-4 py-3 text-right text-text-muted">
                    {formatMoney(it.unitPriceMinorUsd)}
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-text-primary">
                    {formatMoney(it.lineTotalMinorUsd)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-border">
                <td className="px-4 py-3 text-text-muted" colSpan={4}>
                  Subtotal (USD)
                </td>
                <td className="px-4 py-3 text-right text-text-primary">
                  {formatMoney(order.subtotalMinorUsd)}
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 font-semibold text-text-primary" colSpan={4}>
                  Total (USD)
                </td>
                <td className="px-4 py-3 text-right text-lg font-semibold text-text-primary">
                  {formatMoney(order.totalMinorUsd)}
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-text-muted" colSpan={4}>
                  Equivalente (VES) · tasa {order.exchangeRate.rate}
                </td>
                <td className="px-4 py-3 text-right font-medium text-text-primary">
                  {formatMoney(vesTotal, CurrencyCode.VES)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

/** What to pick: product, code and quantity. Warehouse never gets prices (the API leaves them out). */
function PickItemsCard({ items }: { items: ReadonlyArray<WarehouseOrderItem | OrderItem> }) {
  return (
    <Card className="overflow-hidden">
      <CardHeader>
        <CardTitle>Artículos</CardTitle>
      </CardHeader>
      <CardContent className="px-0 pb-0 pt-0">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-border text-xs uppercase tracking-wide text-text-muted">
                <th className="px-4 py-3 font-medium">Producto</th>
                <th className="px-4 py-3 font-medium">SKU</th>
                <th className="px-4 py-3 text-right font-medium">Cant.</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.lineNumber} className="border-b border-border last:border-0">
                  <td className="px-4 py-3 font-medium text-text-primary">{it.productName}</td>
                  <td className="px-4 py-3 font-mono text-xs text-text-muted">{it.skuCode}</td>
                  <td className="px-4 py-3 text-right text-text-muted">{it.quantity}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

/** Order metadata + the locked exchange rate (immutable once created). */
function OrderSummaryCard({
  order,
  customerName,
  salesRepName,
}: {
  order: OrderView;
  customerName?: string;
  salesRepName?: string;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Resumen</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <dl className="flex flex-col">
          <DetailRow label="Cliente">
            {customerName ?? <AccountName id={order.customerId} />}
          </DetailRow>
          {salesRepName ? <DetailRow label="Vendedor">{salesRepName}</DetailRow> : null}
          <DetailRow label="Estado">
            <StatusPill kind="order" value={order.status} />
          </DetailRow>
          <DetailRow label="Moneda">{order.currency}</DetailRow>
          {isPriced(order) ? (
            <DetailRow label="Tasa de cambio">{order.exchangeRate.rate} VES/USD</DetailRow>
          ) : null}
          <DetailRow label="Creado">{formatDateTime(order.createdAt)}</DetailRow>
          <DetailRow label="Última actualización">{formatDateTime(order.updatedAt)}</DetailRow>
          {order.notes ? <DetailRow label="Notas">{order.notes}</DetailRow> : null}
        </dl>
      </CardContent>
    </Card>
  );
}

/** Reservation rows (what stock the order holds and where). */
function ReservationsCard({
  reservations,
  items,
}: {
  reservations: OrderReservation[];
  items: ReadonlyArray<OrderItem | WarehouseOrderItem>;
}) {
  // The reservation only carries the SKU id: show the product and code from the order's own lines.
  const lineBySku = new Map(items.map((i) => [i.skuId, i]));
  return (
    <Card>
      <CardHeader>
        <CardTitle>Reservas de inventario</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {reservations.length === 0 ? (
          <p className="py-2 text-sm text-text-muted">Este pedido no tiene reservas de stock.</p>
        ) : (
          <ul className="flex flex-col">
            {reservations.map((r) => (
              <li
                key={`${r.skuId}:${r.locationId}`}
                className="flex items-center justify-between gap-3 border-b border-border py-3 text-sm last:border-0"
              >
                <div className="flex flex-col">
                  <span className="font-medium text-text-primary">{lineBySku.get(r.skuId)?.productName ?? r.skuId}</span>
                  {lineBySku.get(r.skuId) ? (
                    <span className="font-mono text-xs text-text-muted">{lineBySku.get(r.skuId)?.skuCode}</span>
                  ) : null}
                  <span className="text-xs text-text-muted">{placeLabel(r.locationId)}</span>
                </div>
                <span className="font-medium text-text-primary">{r.quantity} u.</span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Role- and status-aware action bar. Submit (draft) for internal actors or the
 * owner; approve/reject (pending_approval) for approval roles; cancel for any
 * non-terminal order by an internal actor. Each is purely a UX affordance.
 */
function OrderActions({ order }: { order: OrderView }) {
  const { profile } = useSessionProfile();
  const role = profile?.role;

  const submit = useSubmitOrder(order.orderId);
  const approve = useApproveOrder(order.orderId);
  const reject = useRejectOrder(order.orderId);
  const cancel = useCancelOrder(order.orderId);

  const [rejectReason, setRejectReason] = useState('');
  const [showReject, setShowReject] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  if (!role) return null;

  const isApprover = canApproveOrders(role);
  const isInternal = INTERNAL_ACTOR_ROLES.includes(role);
  const isCustomerOwner = role === UserRole.CUSTOMER_USER;

  const canSubmit = order.status === OrderStatus.DRAFT && (isInternal || isCustomerOwner);
  const canDecide = order.status === OrderStatus.PENDING_APPROVAL && isApprover;
  const canCancel = canCancelOrder(role, order.status);

  // Read-only viewers (finance/warehouse/distributor with no actionable state).
  if (!canSubmit && !canDecide && !canCancel) return null;

  const pending = submit.isPending || approve.isPending || reject.isPending || cancel.isPending;

  return (
    <Card>
      <CardContent className="flex flex-col gap-4 pt-5">
        <div className="flex flex-wrap gap-2">
          {canSubmit ? (
            <Button onClick={() => submit.mutate({})} disabled={pending}>
              {submit.isPending ? 'Enviando…' : 'Enviar pedido'}
            </Button>
          ) : null}

          {canDecide ? (
            <>
              <Button onClick={() => approve.mutate({})} disabled={pending}>
                {approve.isPending ? 'Aprobando…' : 'Aprobar'}
              </Button>
              <Button variant="outline" onClick={() => setShowReject((v) => !v)} disabled={pending}>
                Rechazar
              </Button>
            </>
          ) : null}

          {canCancel ? (
            <Button variant="ghost" onClick={() => setConfirmCancel(true)} disabled={pending}>
              Cancelar pedido
            </Button>
          ) : null}
        </div>

        {canDecide && showReject ? (
          <div className="flex flex-col gap-3 rounded-md border border-border p-3">
            <TextareaField
              label="Motivo del rechazo"
              placeholder="Explica por qué se rechaza (opcional)."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowReject(false)} disabled={pending}>
                Cerrar
              </Button>
              <Button
                size="sm"
                onClick={() =>
                  reject.mutate(
                    { reason: rejectReason.trim() || undefined },
                    { onSuccess: () => setShowReject(false) },
                  )
                }
                disabled={pending}
              >
                {reject.isPending ? 'Rechazando…' : 'Confirmar rechazo'}
              </Button>
            </div>
          </div>
        ) : null}

        <ConfirmDialog
          open={confirmCancel}
          title={`Cancelar ${orderLabel(order)}`}
          confirmLabel="Cancelar pedido"
          danger
          onConfirm={() => cancel.mutateAsync({ reason: cancelReason.trim() || undefined })}
          onClose={() => {
            setConfirmCancel(false);
            setCancelReason('');
          }}
        >
          <div className="flex flex-col gap-3">
            <p>
              El pedido queda cancelado y se libera el stock que tenía reservado. No se puede
              deshacer.
            </p>
            <TextareaField
              label="Motivo (opcional)"
              placeholder="Por qué se cancela el pedido."
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
            />
          </div>
        </ConfirmDialog>
      </CardContent>
    </Card>
  );
}

/**
 * Warehouse fulfillment action group (preparar → despachar → entregar). Shown by
 * status + role as a UX affordance — the backend re-checks every transition and
 * stays the sole authority. Large tap targets: warehouse staff act from phones/
 * tablets. Despachar warns that stock ships; Entregar takes an optional note.
 */
function FulfillmentActions({ order }: { order: OrderView }) {
  const { profile } = useSessionProfile();
  const role = profile?.role;

  const pick = usePickOrder(order.orderId);
  const dispatch = useDispatchOrder(order.orderId);
  const deliver = useDeliverOrder(order.orderId);

  const [confirmDispatch, setConfirmDispatch] = useState(false);
  const [showDeliver, setShowDeliver] = useState(false);
  const [deliveryNote, setDeliveryNote] = useState('');

  if (!role) return null;

  const canFulfill = FULFILLMENT_ROLES.includes(role);
  const canDeliverRole = DELIVERY_ROLES.includes(role);

  // Picking can begin while the order is still submitted or already approved.
  const canPick =
    canFulfill &&
    (order.status === OrderStatus.SUBMITTED || order.status === OrderStatus.APPROVED);
  // Dispatch is allowed from approved/picking (and submitted, per the backend contract).
  const canDispatch =
    canFulfill &&
    (order.status === OrderStatus.APPROVED ||
      order.status === OrderStatus.PICKING ||
      order.status === OrderStatus.SUBMITTED);
  const canDeliver = canDeliverRole && order.status === OrderStatus.DISPATCHED;

  if (!canPick && !canDispatch && !canDeliver) return null;

  const pending = pick.isPending || dispatch.isPending || deliver.isPending;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Despacho</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 pt-0">
        <div className="flex flex-wrap gap-3">
          {canPick ? (
            <Button
              className="min-h-12 px-6 text-base"
              onClick={() => pick.mutate({})}
              disabled={pending}
            >
              {pick.isPending ? 'Preparando…' : 'Preparar'}
            </Button>
          ) : null}

          {canDispatch ? (
            <Button
              className="min-h-12 px-6 text-base"
              variant="outline"
              onClick={() => setConfirmDispatch((v) => !v)}
              disabled={pending}
            >
              Despachar
            </Button>
          ) : null}

          {canDeliver ? (
            <Button
              className="min-h-12 px-6 text-base"
              onClick={() => setShowDeliver((v) => !v)}
              disabled={pending}
            >
              Entregar
            </Button>
          ) : null}
        </div>

        {canDispatch && confirmDispatch ? (
          <div className="flex flex-col gap-3 rounded-md border border-warning-soft bg-warning-soft/50 p-4">
            <p className="text-sm font-medium text-text-primary">
              Al despachar, el stock reservado sale del almacén. Esta acción no se puede deshacer.
            </p>
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                variant="ghost"
                className="min-h-11"
                onClick={() => setConfirmDispatch(false)}
                disabled={pending}
              >
                Cancelar
              </Button>
              <Button
                className="min-h-11 px-6"
                onClick={() =>
                  dispatch.mutate({}, { onSuccess: () => setConfirmDispatch(false) })
                }
                disabled={pending}
              >
                {dispatch.isPending ? 'Despachando…' : 'Confirmar despacho'}
              </Button>
            </div>
          </div>
        ) : null}

        {canDeliver && showDeliver ? (
          <div className="flex flex-col gap-3 rounded-md border border-border p-4">
            <TextareaField
              label="Nota de entrega (opcional)"
              placeholder="Quién recibió, observaciones, etc."
              value={deliveryNote}
              onChange={(e) => setDeliveryNote(e.target.value)}
            />
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                variant="ghost"
                className="min-h-11"
                onClick={() => setShowDeliver(false)}
                disabled={pending}
              >
                Cancelar
              </Button>
              <Button
                className="min-h-11 px-6"
                onClick={() =>
                  deliver.mutate(
                    { reason: deliveryNote.trim() || undefined },
                    { onSuccess: () => setShowDeliver(false) },
                  )
                }
                disabled={pending}
              >
                {deliver.isPending ? 'Confirmando…' : 'Confirmar entrega'}
              </Button>
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

/**
 * "Generar factura" — finance/admin only, for a billable order (anything past
 * draft that wasn't cancelled/rejected). Creation is idempotent per order
 * server-side, so a repeat click returns the existing invoice. On success we
 * jump to the new invoice's detail (carrying `?customerId=`, the partition the
 * detail read needs). UX affordance only — the backend re-checks role + state.
 */
function GenerateInvoiceAction({ order, invoicesBase }: { order: OrderView; invoicesBase: string }) {
  const { profile } = useSessionProfile();
  const navigate = useNavigate();
  const create = useCreateInvoiceFromOrder();

  const canBill = profile ? BILLING_ROLES.includes(profile.role) : false;
  if (!canBill || NON_BILLABLE.includes(order.status)) return null;

  return (
    <div className="flex flex-col items-end gap-1">
      <Button
        size="sm"
        variant="outline"
        disabled={create.isPending}
        onClick={() =>
          create.mutate(
            { orderId: order.orderId, kind: 'invoice' },
            {
              onSuccess: (invoice) =>
                navigate({
                  pathname: `${invoicesBase}/${invoice.invoiceId}`,
                  search: `?customerId=${encodeURIComponent(invoice.customerId)}`,
                }),
            },
          )
        }
      >
        {create.isPending ? 'Generando…' : 'Generar factura'}
      </Button>
    </div>
  );
}

/** Local date-time (es-VE); raw value fallback if unparseable. */
function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString('es-VE', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
}
